import React, { useCallback, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useHistory, useLocation } from 'react-router-dom';

import { StoreProgramParticipantAdvance } from '../../../../generated/StoreProgramParticipantAdvance';
import { routePaths } from '../../constants/routes';
import { colors, spacing } from '../../constants/theme';
import { Text } from '../../core-ui';

import { ProgramParticipantForm } from './ProgramParticipantForm';
import {
  ChecklistAnswer,
  ProgramQuestionaireForm,
  setChecklistAnswer,
} from './ProgramQuestionaireForm';
import { ScreenFilters } from './newProgramParticipantPage';

type ParticipantRow = StoreProgramParticipantAdvance['storeProgramParticipantAdvance']['row'][0];

type LocationState = {
  programName?: string;
  programId?: string;
  data?: ParticipantRow;
  viewOnly?: boolean;
  filters?: ScreenFilters;
};

export function WipParticipant() {
  const history = useHistory();
  const location = useLocation<LocationState>();

  const participantData = location.state?.data;
  const programName =
    location.state?.programName ?? participantData?.StoreProgram?.name ?? '';
  const storeName = participantData?.store?.storeName ?? '';
  const viewOnly = location.state?.viewOnly;
  const currentFilters = location.state?.filters;

  const questionaire = participantData?.checklistQuestions ?? [];
  type ChecklistQuestion = typeof questionaire[0];
  const participantChecklists = useMemo(() => {
    let attachments = participantData?.attachments;
    let preferenceCheckList = participantData?.preferenceCheckList ?? [];
    let phoneNumbers = [
      attachments?.firstParticipantPhoneNumber,
      attachments?.secondParticipantPhoneNumber,
    ].filter((phoneNumber): phoneNumber is string => !!phoneNumber);
    if (phoneNumbers.length === 0) {
      // NOTE: still render one empty form so the questions are visible
      return [[]];
    }
    return phoneNumbers.map(
      (phoneNumber) =>
        preferenceCheckList.find((p) => p.phoneNumber === phoneNumber)
          ?.checklistData ?? [],
    );
  }, [participantData]);

  const [formDirty, setFormDirty] = useState(false);
  const [editable, setEditable] = useState(false);
  const [checklistAnswers, setChecklistAnswers] = useState<
    Array<Array<ChecklistAnswer>>
  >(participantChecklists);

  const handleEditableChange = useCallback(
    (isEditable: boolean) => {
      setEditable(isEditable);
      // NOTE: discard unsaved answers, same as the participant form
      setChecklistAnswers(participantChecklists);
    },
    [participantChecklists],
  );

  const handleAnswerChange = useCallback(
    (checklistIndex: number, question: ChecklistQuestion, answer: string) => {
      setChecklistAnswers((prev) =>
        prev.map((answers, index) =>
          index === checklistIndex
            ? setChecklistAnswer(answers, question, answer)
            : answers,
        ),
      );
    },
    [],
  );

  const handleGoBack = useCallback(() => {
    history.replace({
      pathname: routePaths.newProgramParticipant,
      state: {
        filters: currentFilters,
      },
    });
  }, [currentFilters, history]);

  if (!participantData) {
    return (
      <View style={styles.root}>
        <Text color="grey">Data tidak ditemukan.</Text>
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.root}>
      <View style={styles.breadcrumb}>
        <TouchableOpacity onPress={handleGoBack}>
          <Text color="blue" size="s">
            Program Principal
          </Text>
        </TouchableOpacity>
        <Text size="s" style={styles.breadcrumbSeparator}>
          /
        </Text>
        <TouchableOpacity onPress={handleGoBack}>
          <Text size="s" color="blue">
            {programName}
          </Text>
        </TouchableOpacity>
        <Text size="s" style={styles.breadcrumbSeparator}>
          /
        </Text>
        <Text size="s" color="blue">
          {storeName}
        </Text>
      </View>
      <View style={styles.formRow}>
        <View style={styles.formContainer}>
          <ProgramParticipantForm
            viewMode={viewOnly}
            data={participantData}
            refetchData={handleGoBack}
            onEditableChange={handleEditableChange}
            checklistAnswers={checklistAnswers}
            onFormHasChange={(changed) => {
              if (changed !== formDirty) {
                setFormDirty(changed);
              }
            }}
          />
        </View>
        {questionaire.length > 0 && (
          <View style={styles.formContainer}>
            {checklistAnswers.map((checklistData, index) => (
              <View
                key={index}
                style={index > 0 ? styles.questionaireSpacing : undefined}
              >
                <ProgramQuestionaireForm
                  title={`Checklist Preferensi Peserta ${index + 1}`}
                  questionaire={questionaire}
                  answers={checklistData}
                  editable={editable}
                  onAnswerChange={(question, answer) =>
                    handleAnswerChange(index, question, answer)
                  }
                />
              </View>
            ))}
          </View>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  root: {
    flexGrow: 1,
    backgroundColor: colors.background.grey,
    padding: spacing.l,
    alignItems: 'center',
  },

  formRow: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  formContainer: {
    flex: 1,
    minWidth: 400,
    maxWidth: 600,
    marginHorizontal: spacing.s,
    marginBottom: spacing.l,
  },
  questionaireSpacing: {
    marginTop: spacing.l,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.s,
    alignSelf: 'flex-start',
  },
  breadcrumbSeparator: {
    marginHorizontal: spacing.xxs,
  },
});
